import { z } from "zod";
import { Cents, IsoUtc, PropertyId, Uuid } from "./primitives";
import { HomeLook, PlacedItem, VisitorAccess } from "./properties";
import { DecorationAnchorType, HomeStageId } from "./world";

/**
 * Homes round (founder feedback 2026-10-10): sequential home upgrades sold at Townsy Home Builders,
 * yard features and furniture as owned inventory placed on predefined anchors, and free look choices.
 * Coins and dollars are independent offers (never an exchange rate). Dollar offers are shown but
 * disabled until the payments milestone.
 */
export const CatalogItemKind = z.enum(["YARD", "FURNITURE"]);
export type CatalogItemKind = z.infer<typeof CatalogItemKind>;
export const CatalogShop = z.enum(["home_builders", "furnishings"]);
export type CatalogShop = z.infer<typeof CatalogShop>;

export const Offer = z.object({ coins: z.number().int().min(0).nullable(), usdCents: Cents.nullable() });
export type Offer = z.infer<typeof Offer>;

export const CatalogItemDto = z.object({
  itemId: z.string().min(1).max(40),
  kind: CatalogItemKind,
  shop: CatalogShop,
  name: z.string(),
  description: z.string(),
  /** Where it can be placed. */
  anchorTypes: z.array(DecorationAnchorType).min(1),
  offer: Offer,
  catalogVersion: z.number().int().min(1),
});
export type CatalogItemDto = z.infer<typeof CatalogItemDto>;

export const HomeStageOfferDto = z.object({
  stage: HomeStageId,
  name: z.string(),
  rank: z.number().int().min(0),
  /** Price to upgrade INTO this stage from the previous one (tent is included with the plot: null). */
  offer: Offer.nullable(),
});
export type HomeStageOfferDto = z.infer<typeof HomeStageOfferDto>;

/** GET /api/v1/catalog */
export const CatalogResponse = z.object({
  catalogVersion: z.number().int().min(1),
  stages: z.array(HomeStageOfferDto),
  items: z.array(CatalogItemDto),
  dollarPurchasesEnabled: z.boolean(),
});
export type CatalogResponse = z.infer<typeof CatalogResponse>;

/** POST /api/v1/catalog/items/:itemId/purchase (Idempotency-Key required). Buying adds ONE item to inventory. */
export const CatalogPurchaseRequest = z
  .object({ unit: z.literal("COIN"), expectedPriceCoins: z.number().int().min(0) })
  .strict();
export type CatalogPurchaseRequest = z.infer<typeof CatalogPurchaseRequest>;
export const CatalogPurchaseResponse = z.object({
  orderId: Uuid,
  status: z.literal("fulfilled"),
  itemId: z.string(),
  inventoryInstanceId: Uuid,
  spentCoins: z.number().int().min(0),
  coinsAvailableAfter: z.number().int().min(0),
});
export type CatalogPurchaseResponse = z.infer<typeof CatalogPurchaseResponse>;

/** POST /api/v1/homes/:propertyId/upgrade (Idempotency-Key required). Only the NEXT stage is accepted. */
export const HomeUpgradeRequest = z
  .object({ toStage: HomeStageId, unit: z.literal("COIN"), expectedPriceCoins: z.number().int().min(0) })
  .strict();
export type HomeUpgradeRequest = z.infer<typeof HomeUpgradeRequest>;
export const HomeUpgradeResponse = z.object({
  orderId: Uuid,
  status: z.literal("fulfilled"),
  propertyId: PropertyId,
  stage: HomeStageId,
  spentCoins: z.number().int().min(0),
  coinsAvailableAfter: z.number().int().min(0),
});
export type HomeUpgradeResponse = z.infer<typeof HomeUpgradeResponse>;

/** PATCH /api/v1/homes/:propertyId/look — free cosmetic choices, versioned with the layout. */
export const HomeLookUpdateRequest = z.object({ look: HomeLook, expectedLayoutVersion: z.number().int().min(0) }).strict();
export type HomeLookUpdateRequest = z.infer<typeof HomeLookUpdateRequest>;

/**
 * PATCH /api/v1/homes/:propertyId/access — the home's Open / Closed switch (ADR-0019; it replaced the "private" flag of
 * ADR-0005: an existing private home is closed). Closing never moves anyone out; that is "Send visitors out".
 */
export const HomeAccessRequest = z.object({ access: VisitorAccess }).strict();
export type HomeAccessRequest = z.infer<typeof HomeAccessRequest>;

/** POST /api/v1/homes/:propertyId/decorations/place (owner; item must be owned and unplaced; anchor must accept it). */
export const DecorationPlaceRequest = z
  .object({
    inventoryInstanceId: Uuid,
    scope: z.enum(["yard", "interior"]),
    anchorId: z.string().min(1).max(32),
    rotation: z.number(),
    expectedLayoutVersion: z.number().int().min(0),
  })
  .strict();
export type DecorationPlaceRequest = z.infer<typeof DecorationPlaceRequest>;

/** POST /api/v1/homes/:propertyId/decorations/remove (returns the item to inventory; never deletes it). */
export const DecorationRemoveRequest = z
  .object({ scope: z.enum(["yard", "interior"]), anchorId: z.string().min(1).max(32), expectedLayoutVersion: z.number().int().min(0) })
  .strict();
export type DecorationRemoveRequest = z.infer<typeof DecorationRemoveRequest>;

/** POST /api/v1/homes/:propertyId/reset-layout: every placed item goes back to inventory. */
export const ResetLayoutRequest = z.object({ expectedLayoutVersion: z.number().int().min(0) }).strict();
export type ResetLayoutRequest = z.infer<typeof ResetLayoutRequest>;

export const InventoryItem = z.object({
  inventoryInstanceId: Uuid,
  itemId: z.string(),
  kind: CatalogItemKind,
  name: z.string(),
  acquiredAt: IsoUtc,
  /** Where it is placed right now, or null when it is in storage. */
  placement: z.object({ propertyId: PropertyId, scope: z.enum(["yard", "interior"]), anchorId: z.string() }).nullable(),
});
export type InventoryItem = z.infer<typeof InventoryItem>;
/** GET /api/v1/me/inventory */
export const InventoryResponse = z.object({ items: z.array(InventoryItem) });
export type InventoryResponse = z.infer<typeof InventoryResponse>;

/** GET /api/v1/me/home — the signed-in resident's home (null when they own no plot). */
export const MyHomeResponse = z.object({
  home: z
    .object({
      propertyId: PropertyId,
      address: z.string(),
      stage: HomeStageId,
      layoutVersion: z.number().int().min(0),
      /** Open / Closed to visitors (ADR-0019). */
      access: VisitorAccess,
      look: HomeLook,
      placedItems: z.array(PlacedItem),
      /** The next stage and its price, or null at the top stage (castle). */
      nextUpgrade: HomeStageOfferDto.nullable(),
    })
    .nullable(),
});
export type MyHomeResponse = z.infer<typeof MyHomeResponse>;

/** Mutations that change a home return the fresh state. */
export const HomeStateResponse = z.object({
  propertyId: PropertyId,
  layoutVersion: z.number().int().min(0),
  stage: HomeStageId,
  access: VisitorAccess,
  look: HomeLook,
  placedItems: z.array(PlacedItem),
});
export type HomeStateResponse = z.infer<typeof HomeStateResponse>;
